import { graphql, HeadProps, PageProps } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import React, { useEffect } from 'react'
import { FAQPage, Menu, WebSite, WithContext } from 'schema-dts'
import { CtaPopupZone } from 'src/components/pageLayout/ctaPopupZone'
import DocumentHead from 'src/components/pageLayout/documentHead'
import SliceZone from 'src/components/pageLayout/sliceZone'
import {
  PageQuery,
  PrismicCtaPopup,
  PrismicPage,
  PrismicSiteInformation,
  Site,
} from 'src/typings/generated/graphql'
import {
  buildFaqMainEntityArray,
  buildHasMenuItemsArray,
  parseSlicesForSchemaType,
  SchemaType,
} from 'src/utils/prismicPageHelper'
import * as Styles from './{prismicPage.url}.module.scss'

const PageTemplate = ({ data }: PageProps<PageQuery>) => {
  const page = data.prismicPage as PrismicPage

  const redirectUrl = page.data.redirect_url?.url
  const ctaPopup = page.data.cta_popup?.document as PrismicCtaPopup

  useEffect(() => {
    const redirectPage = () => {
      if (typeof window !== 'undefined' && redirectUrl) {
        window.location.href = redirectUrl
      }
    }
    setTimeout(() => {
      redirectPage()
    }, 500)
  }, [])

  if (!data) return null

  return (
    <div data-testid="page-template">
      {redirectUrl && (
        <div className={Styles.loaderContainer}>
          <div className={Styles.spinner} />
          <p>You are being redirected...</p>
          <div />
        </div>
      )}
      {!redirectUrl && <SliceZone sliceZone={page.data.body} />}
      {ctaPopup && <CtaPopupZone ctaPopup={ctaPopup} />}
    </div>
  )
}

export default withPrismicPreview(PageTemplate)

export const Head = ({ data }: HeadProps<PageQuery>) => {
  const { prismicPage, prismicSiteInformation, site } = data

  const { data: pageData, url } = prismicPage as PrismicPage
  const { siteMetadata } = site as Site
  const siteInformation = prismicSiteInformation as PrismicSiteInformation

  const title =
    pageData.meta_title ?? pageData.document_display_name?.text ?? 'Blog'
  const description =
    pageData.meta_description ??
    siteInformation.data.site_description ??
    siteMetadata?.description ??
    ''
  const imageUrl =
    pageData.meta_image?.url ??
    prismicSiteInformation?.data.site_icon?.url ??
    siteMetadata?.logoPath ??
    ''
  const noIndex = pageData.meta_no_index ?? undefined
  const schemaType = parseSlicesForSchemaType(pageData.body)
  let schema: WithContext<Menu | WebSite | FAQPage>

  if (schemaType === SchemaType.menu) {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'Menu',
      url: prismicSiteInformation?.data.menu_page?.url ?? '',
      name: 'Raising Canes Chicken Fingers Menu',
      image: prismicSiteInformation?.data.site_icon?.url ?? '',
      description,
      hasMenuItem: buildHasMenuItemsArray(
        pageData.body,
        prismicPage?.url ?? ''
      ),
    }
  } else if (schemaType === SchemaType.faq) {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: buildFaqMainEntityArray(),
    }
  } else {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: url ?? '',
      name: title,
      description,
      image: {
        '@type': 'ImageObject',
        url: imageUrl,
      },
    }
  }

  return DocumentHead({
    title,
    url: url ?? '',
    description,
    imageUrl,
    pageSchema: schema,
    noIndex,
  })
}

export const query = graphql`
  query Page($id: String) {
    prismicPage(id: { eq: $id }) {
      _previewable
      data {
        document_display_name {
          text
        }
        cta_popup {
          document {
            ... on PrismicCtaPopup {
              id
              uid
              type
              data {
                headline
                body
                cta_label
                reject_button_label
                image_mode
                image {
                  gatsbyImageData
                  alt
                }
                cta_link {
                  ...BasicLinkFragment
                }
                is_enabled
              }
            }
          }
        }
        redirect_url {
          url
        }
        body {
          ... on PrismicSliceType {
            slice_type
          }
          ...PageDataBodyArtOfAPerfectMeal
          ...PageDataBodyArtOfAPerfectMealV2
          ...PageDataBodyArticlesHero
          ...PageDataBodyArticlesLibrary
          ...PageDataBodyBannerCta
          ...PageDataBodyBasicHero
          ...PageDataBodyBasicSlider
          ...PageDataBodyCallToAction
          ...PageDataBodyCardsCta
          ...PageDataBodyCateringOptions
          ...PageDataBodyChickenFingerCalculator
          ...PageDataBodyCollageStatsCallout
          ...PageDataBodyCommunityInvolvement
          ...PageDataBodyCommunitySuccessStories
          ...PageDataBodyConfigurableCta
          ...PageDataBodyContactColumns
          ...PageDataBodyCountdownBanner
          ...PageDataBodyCrewCallout
          ...PageDataBodyCrewRising
          ...PageDataBodyDescriptiveSlider
          ...PageDataBodyDoubleCta
          ...PageDataBodyDownloadAssets
          ...PageDataBodyDynamicCta
          ...PageDataBodyECardWidget
          ...PageDataBodyEmbeddedContent
          ...PageDataBodyEmbeddedContentGrid
          ...PageDataBodyFaq
          ...PageDataBodyFaqCategory
          ...PageDataBodyFaqCta
          ...PageDataBodyGlitterEffect
          ...PageDataBodyHeadline
          ...PageDataBodyHeroSlider
          ...PageDataBodyHighlights
          ...PageDataBodyHostAFundraiser
          ...PageDataBodyHoverCardGrid
          ...PageDataBodyHowItWorks
          ...PageDataBodyImageGrid
          ...PageDataBodyIconCardGrid
          ...PageDataBodyImageGallery
          ...PageDataBodyImageReel
          ...PageDataBodyImageStrip
          ...PageDataBodyLinkGrid
          ...PageDataBodyLocator
          ...PageDataBodyLogoCta
          ...PageDataBodyWorldCupLocator
          ...PageDataBodyMenuCarousel
          ...PageDataBodyMenuItemGroup
          ...PageDataBodyMinimalHero
          ...PageDataBodyMultiMediaGrid
          ...PageDataBodyMultiMediaImageGrid
          ...PageDataBodyNewsroomArticlesLibrary
          ...PageDataBodyProfileHighlight
          ...PageDataBodyQuote
          ...PageDataBodyQuoteBlock
          ...PageDataBodyRecentArticles
          ...PageDataBodyRecruitingCta
          ...PageDataBodyRichTextCta
          ...PageDataBodySaucyCarousel
          ...PageDataBodySearchableFaq
          ...PageDataBodySecondaryNavigation
          ...PageDataBodyStaggeredImageCards
          ...PageDataBodyStatGrid
          ...PageDataBodyStoryCarousel
          ...PageDataBodyTimeline
          ...PageDataBodyTitleAndDescription
          ...PageDataBodyUpcomingRestaurants
          ...PageDataBodyTable
        }
        meta_title
        meta_description
        meta_image {
          url
        }
        meta_no_index
      }
      url
    }
    prismicSiteInformation {
      data {
        site_icon {
          url
        }
        site_description
        menu_page {
          url
        }
      }
    }
    site {
      siteMetadata {
        description
        logoPath
      }
    }
  }
`
